import React from 'react';
import { CircularProgress } from '@mui/material';
import { useIntentGraph } from './useCPData';
import './ArtemyStyles.css';

// Static demo graph data — replaced by live data when backend is ready
const DEMO_NODES = [
  { id: 'okr-q1', label: 'OKR-Q1', sub: 'Revenue', x: 350, y: 55, type: 'OKR', color: 'var(--artemy-accent)', r: 26 },
  { id: 'prd-109', label: 'PRD-109', sub: 'Checkout', x: 150, y: 130, type: 'PRD', color: 'var(--artemy-green)', r: 21 },
  { id: 'prd-112', label: 'PRD-112', sub: 'Payments', x: 350, y: 130, type: 'PRD', color: 'var(--artemy-yellow)', r: 21, drift: true },
  { id: 'prd-118', label: 'PRD-118', sub: 'Analytics', x: 550, y: 130, type: 'PRD', color: 'var(--artemy-green)', r: 21 },
  { id: 'ep-41', label: 'EP-41', x: 80, y: 210, type: 'Epic', color: 'var(--artemy-accent)' },
  { id: 'ep-42', label: 'EP-42', x: 200, y: 210, type: 'Epic', color: 'var(--artemy-accent)' },
  { id: 'st-201', label: 'ST-201', x: 300, y: 210, type: 'Story', color: 'var(--artemy-accent)' },
  { id: 'st-202', label: 'ST-202', x: 400, y: 210, type: 'Story', color: 'var(--artemy-yellow)', drift: true },
  { id: 'ep-55', label: 'EP-55', x: 500, y: 210, type: 'Epic', color: 'var(--artemy-accent)' },
  { id: 'ep-56', label: 'EP-56', x: 620, y: 210, type: 'Epic', color: 'var(--artemy-accent)' },
];
const DEMO_EDGES = [
  { from: 'okr-q1', to: 'prd-109' }, { from: 'okr-q1', to: 'prd-112' }, { from: 'okr-q1', to: 'prd-118' },
  { from: 'prd-109', to: 'ep-41' }, { from: 'prd-109', to: 'ep-42' },
  { from: 'prd-112', to: 'st-201' }, { from: 'prd-112', to: 'st-202' },
  { from: 'prd-118', to: 'ep-55' }, { from: 'prd-118', to: 'ep-56' },
];

function NodeShape({ node }) {
  if (node.type === 'OKR' || node.type === 'PRD') {
    return (
      <g>
        <circle cx={node.x} cy={node.y} r={node.r} fill="rgba(0,0,0,.4)" stroke={node.color} strokeWidth={node.drift ? 2 : 1.5} strokeDasharray={node.drift ? '4,2' : undefined} />
        <text x={node.x} y={node.y - 5} fill={node.color} fontSize={8} fontFamily="var(--artemy-font-mono)" textAnchor="middle" style={{ pointerEvents: 'none' }}>{node.label}</text>
        <text x={node.x} y={node.y + 7} fill="var(--artemy-text-primary)" fontSize={8} fontFamily="var(--artemy-font-mono)" textAnchor="middle" style={{ pointerEvents: 'none' }}>{node.sub}</text>
        {node.drift && <text x={node.x + node.r - 6} y={node.y - node.r + 4} fill="var(--artemy-yellow)" fontSize={12} style={{ pointerEvents: 'none' }}>⚠</text>}
      </g>
    );
  }
  const w = 46, h = 28;
  return (
    <g>
      <rect x={node.x - w / 2} y={node.y - h / 2} width={w} height={h} rx={6} fill="rgba(17,20,32, 0.8)" stroke={node.drift ? 'var(--artemy-yellow)' : 'var(--artemy-border-bright)'} strokeWidth={node.drift ? 2 : 1} strokeDasharray={node.drift ? '4,2' : undefined} />
      <text x={node.x} y={node.y + 4} fill={node.drift ? 'var(--artemy-yellow)' : 'var(--artemy-text-secondary)'} fontSize={8.5} fontFamily="var(--artemy-font-mono)" textAnchor="middle" style={{ pointerEvents: 'none' }}>{node.label}</text>
      {node.drift && <text x={node.x + 16} y={node.y - h / 2 + 8} fill="var(--artemy-yellow)" fontSize={11} style={{ pointerEvents: 'none' }}>⚠</text>}
    </g>
  );
}

export default function IntentGraph() {
  const { graph, loading } = useIntentGraph();

  const nodes = (graph?.nodes?.length ? graph.nodes : DEMO_NODES);
  const edges = (graph?.edges?.length ? graph.edges : DEMO_EDGES);
  const drifts = nodes.filter((n) => n.drift).length;
  const orphaned = graph?.orphaned || 17;

  const getNode = (id) => nodes.find((n) => n.id === id);

  return (
    <div className="artemy-fade-up">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 22 }}>
        <div>
          <div className="artemy-page-title">Intent Graph</div>
          <div className="artemy-page-sub">Semantic map: business objectives → epics → code → outcomes</div>
        </div>
        <button className="artemy-btn artemy-btn-primary">
          + Link Node
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14, marginBottom: 16 }}>
        {[
          { label: 'Total Nodes', value: nodes.length, color: 'var(--artemy-text-primary)' },
          { label: 'OKRs Tracked', value: nodes.filter((n) => n.type === 'OKR').length || 24, color: 'var(--artemy-text-primary)' },
          { label: 'Intent Drifts', value: drifts || 3, color: 'var(--artemy-red)' },
          { label: 'Orphaned Stories', value: orphaned, color: 'var(--artemy-yellow)' },
        ].map((m) => (
          <div key={m.label} className="artemy-card" style={{ padding: 16 }}>
            <div style={{ fontFamily: 'var(--artemy-font-mono)', fontSize: 11, color: 'var(--artemy-text-secondary)', marginBottom: 7 }}>{m.label}</div>
            <div style={{ fontFamily: 'var(--artemy-font-display)', fontSize: 26, fontWeight: 700, color: m.color }}>{m.value}</div>
          </div>
        ))}
      </div>

      <div className="artemy-card" style={{ overflow: 'hidden', marginBottom: 16 }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--artemy-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontFamily: 'var(--artemy-font-display)', fontSize: 14, fontWeight: 600 }}>🔗 Intent Graph Visualizer</div>
          <div style={{ fontFamily: 'var(--artemy-font-mono)', fontSize: 11, color: 'var(--artemy-text-dim)' }}>{loading ? 'Processing…' : 'Live semantic model active'}</div>
        </div>
        <div style={{ padding: 18 }}>
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '48px 0' }}><CircularProgress size={28} sx={{ color: 'var(--artemy-accent)' }} /></div>
          ) : (
            <div style={{ background: 'var(--artemy-bg-base)', borderRadius: 12, overflow: 'hidden', border: '1px solid var(--artemy-border)' }}>
              <svg viewBox="0 0 700 260" width="100%" height="260" style={{ display: 'block' }}>
                {/* Edges */}
                {edges.map((e, i) => {
                  const from = getNode(e.from), to = getNode(e.to);
                  if (!from || !to) return null;
                  const isDrift = from.drift || to.drift;
                  return <line key={i} x1={from.x} y1={from.y} x2={to.x} y2={to.y} stroke={isDrift ? 'var(--artemy-yellow)' : 'var(--artemy-border)'} strokeWidth={isDrift ? 2 : 1} strokeDasharray={isDrift ? '4,4' : '3,3'} />;
                })}
                {/* Nodes */}
                {nodes.map((n) => <NodeShape key={n.id} node={n} />)}
                {/* Legend */}
                <circle cx={30} cy={242} r={5} fill="none" stroke="var(--artemy-accent)" strokeWidth={1.5} />
                <text x={42} y={246} fill="var(--artemy-text-dim)" fontSize={9} fontFamily="var(--artemy-font-mono)">OKR / PRD</text>
                <rect x={125} y={237} width={10} height={10} rx={2} fill="none" stroke="var(--artemy-border-bright)" strokeWidth={1} />
                <text x={141} y={246} fill="var(--artemy-text-dim)" fontSize={9} fontFamily="var(--artemy-font-mono)">Story / Epic</text>
                <line x1={225} y1={242} x2={245} y2={242} stroke="var(--artemy-yellow)" strokeWidth={1.5} strokeDasharray="3,2" />
                <text x={253} y={246} fill="var(--artemy-text-dim)" fontSize={9} fontFamily="var(--artemy-font-mono)">Intent Drift</text>
              </svg>
            </div>
          )}
        </div>
      </div>

      {/* Detected drifts list */}
      {drifts > 0 && (
        <div className="artemy-card" style={{ overflow: 'hidden' }}>
          <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--artemy-border)' }}>
            <div style={{ fontFamily: 'var(--artemy-font-display)', fontSize: 14, fontWeight: 600 }}>⚠ Detected Drifts</div>
          </div>
          {nodes.filter((n) => n.drift).map((n) => (
            <div key={n.id} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 20px', borderBottom: '1px solid rgba(255,255,255,.03)' }}>
              <div style={{ width: 30, height: 30, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, background: 'rgba(245,166,35,.1)' }}>🟡</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--artemy-text-primary)' }}>{n.label} scope expanded beyond documented PRD intent</div>
                <div style={{ fontFamily: 'var(--artemy-font-mono)', fontSize: 11, color: 'var(--artemy-text-dim)', marginTop: 2 }}>Detected automatically · Requires immediate review</div>
              </div>
              <span style={{ fontFamily: 'var(--artemy-font-mono)', fontSize: 10, color: 'var(--artemy-yellow)', background: 'rgba(245,166,35,.1)', padding: '2px 8px', borderRadius: 4, textTransform: 'uppercase' }}>intent drift</span>
            </div>
          ))}
        </div>
      )}
      <div style={{ height: 40 }} />
    </div>
  );
}
